import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { MenuIcon } from "lucide-react";
import { useAuth } from "../../context/AuthProvider";
import LogoutModal from "../LogoutModal";
import NotificationDropdown from "../NotificationDropdown";

const NAV_LINKS = [
  { to: "/stats", label: "Stats", showInCenter: true, permission: "stats" },
  { to: "/dm-stats", label: "Lead Stats", showInCenter: false, permission: "stats" },
  { to: "/transfer-history", label: "Transfer History", permission: "transfer-history" },
  { to: "/leads-tracking", label: "Leads Tracking", permission: "stats" },
  { to: "/calls-dashboard", label: "Calls Dashboard", permission: "call-logs-board" },
  { to: "/call-logs-board", label: "Call Logs Board", permission: "call-logs-board" },
  { to: "/agent-links", label: "Agent Links", showInCenter: true, permission: "agent-links" },
  { to: "/", label: "Property Search", showInCenter: true },
  { to: "/ai-prompts", label: "AI Prompts", permission: "ai-prompts" },
  { to: "/fetched-leads", label: "Found Leads", showInCenter: true, permission: "fetched-leads" },
  { to: "/added-leads", label: "Added Leads", showInCenter: true, permission: "leads" },
  { to: "/wrong-phones", label: "Wrong Phones", showInCenter: true, permission: "leads" },
  { to: "/staff/users", label: "Staff Users", showInCenter: false, permission: "stats" },
  { to: "/claim-leads", label: "Claim Leads", showInCenter: false, permission: "stats" },
  { to: "/compairables", label: "Compairables", showInCenter: false, permission: "leads" },
  // { to: "/mailing-details", label: "Mailing Details", showInCenter: false, permission: "leads" },
  { to: "/flipped-properties", label: "Flipped Properties", showInCenter: false, permission: "leads" },
  {
    label: "Not Added",
    showInCenter: true,
    permission: "leads",
    children: [
      { to: "/not-added-leads", label: "Not Added All" },
      { to: "/not-added-leads-s", label: "Not Added S" },
      { to: "/not-added-leads-m", label: "Not Added M" },
      { to: "/not-added-leads-b", label: "Not Added B" },
      { to: "/not-added-leads-d", label: "Not Added D" },
      { to: "/not-added-leads-other", label: "Not Added Unknown" },
    ],
  },
  { to: "/not-found-s", label: "Not Found S", showInCenter: true, permission: "not-found-list-1" },
  { to: "/not-found-s-2", label: "Not Found S-2", showInCenter: true, permission: "not-found-list-2" },
  { to: "/not-found-m", label: "Not Found M", showInCenter: true, permission: "not-found-list-1" },
  { to: "/not-found-m-2", label: "Not Found M-2", showInCenter: true, permission: "not-found-list-2" },
  // { to: "/not-found-b", label: "Not Found B", showInCenter: true, permission: "not-found-list-1" },
  { to: "/not-found-b-2", label: "Not Found B-2", showInCenter: true, permission: "not-found-list-2" },
  { to: "/owner-links", label: "Owner Links", showInCenter: false, permission: "owner-links" },
  // { to: "/duval", label: "Duval", showInCenter: true, permission: "duval" },
  { to: "/duval-new", label: "Duval", showInCenter: false, permission: "duval" },
  { to: "/facebook-accounts", label: "Facebook Accounts", permission: "facebook-accounts" },
  { to: "/facebook-groups", label: "Facebook Groups", permission: "facebook-groups" },
  { to: "/facebook-posts", label: "Facebook Posts", permission: "facebook-posts" },
  { to: "/facebook-import", label: "Facebook Import", permission: "facebook-import" },
  { to: "/facebook-logs", label: "Facebook Logs", permission: "facebook-logs" },
  { to: "/craigslist", label: "Craigslist", permission: "craigslist" },
  { to: "/map-sheets", label: "Transfer Sheet Data", permission: "transfer-sheet-data" },
  { to: "/agents-list", label: "Agents List", showInCenter: false, permission: "agents-list" },
  { to: "/address-sheets", label: "Address Sheets", permission: "address-sheets" },
  { to: "/creative-finance-calculator", label: "Creative Finance Calculator", permission: "calculations" },
  { to: "/novation-instant-offer-calculator", label: "Novation Instant Offer Calculator", permission: "calculations" },
  { to: "/cash-offer-calculator", label: "Cash Offer Calculator", permission: "calculations" },
  { to: "/calculations", label: "Calculations", permission: "calculations" },
  // { to: "/referral-leads", label: "Referral Leads", permission: "calculations" },
  { to: "/referrals", label: "Referrals", permission: "calculations" },
  // { to: "/marketplace", label: "Marketplace", permission: "agent-links" },
  // { to: "/marketplace/location/1R61hArrhXJcjiRu9RFR", label: "Hidden Fields", permission: "agent-links" },
];

export default function Header() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const dropdownRef = useRef(null);
  const [childrensOpen, setChildrensOpen] = useState(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const isActive = (path, children = []) => {
    if (path === "/" || path === "/property-search") {
      return location.pathname === "/" || location.pathname.startsWith("/property-search") ? "!text-yellow-300" : "!text-white";
    }

    if (children.length > 0) {
      return children.some((child) => location.pathname.startsWith(child.to)) ? "!text-yellow-300" : "!text-white";
    }

    return location.pathname.startsWith(path) ? "!text-yellow-300" : "!text-white";
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const hasPermission = (permName) => {
    if (!permName) return false;
    return user?.permissions?.some((p) => p.permission_name === permName && p.can_view === 1);
  };

  const hasNotPermission = (permName) => {
    if (!permName) return false;
    return !hasPermission(permName);
  };

  const filterLinks = (links) =>
    links.filter((link) => {
      if (link.permission) return hasPermission(link.permission);
      if (link.notPermission) return hasNotPermission(link.notPermission);
      return true; // default case
    });

  const centerLinks = filterLinks(NAV_LINKS.filter((link) => link.showInCenter));
  const menuLinks = filterLinks(NAV_LINKS.filter((link) => !link.showInCenter));

  const isCallsDashboard = location.pathname.includes("calls-dashboard");
  const isDMStats = location.pathname.includes("dm-stats");
  const isVoicemails = location.pathname.includes("voicemails");
  const isLeadsTracking = location.pathname.includes("leads-tracking");

  const activeTheme = isCallsDashboard ? true : isDMStats ? true : isVoicemails ? true : isLeadsTracking ? true : false;

  return (
    <>
      {/* <header
        // className="relative bg-[#008080] shadow flex items-center justify-between pt-4 pb-5 px-5 md:px-8 text-white z-[2] sm:rounded-b-2xl"
        className={`relative shadow flex items-center justify-between pt-4 pb-5 px-5 md:px-8 text-white z-[2] sm:rounded-b-2xl
        ${isCallsDashboard ? "bg-sky-500" : "bg-[#008080]"}
      `}
      >
        <div className="text-3xl font-semibold">Real Estate</div>
        <nav className="hidden md:flex gap-6 relative">
          {centerLinks.map((link, i) => {
            if (link.children?.length) {
              const parentActive = isActive(link.to, link.children);

              return (
                <div key={i} className={`relative group transition ${parentActive.includes("!text-yellow-300") ? "!text-yellow-300" : "hover:text-yellow-300"}`} onMouseEnter={() => setChildrensOpen(i)} onMouseLeave={() => setChildrensOpen(null)}>
                  <button onClick={() => setChildrensOpen(childrensOpen === i ? null : i)} className={`transition cursor-pointer ${parentActive.includes("!text-yellow-300") ? "!text-yellow-300" : ""}`}>
                    {link.label}
                  </button>
                  <div className={`absolute left-0 min-w-42 bg-white rounded-md shadow-lg transform transition-all duration-200 origin-top z-[3] overflow-hidden ${childrensOpen === i ? "scale-100 max-h-96 opacity-100" : "scale-95 max-h-0 opacity-0 pointer-events-none"}`}>
                    {link.children.map((child, i) => (
                      <Link key={i} to={child.to} className={`block !px-4 !py-2 text-md text-center !text-gray-700 hover:bg-blue-100 transition text-nowrap ${location.pathname === child.to ? "bg-blue-100 font-medium !text-blue-700" : ""}`}>
                        {child.label}
                      </Link>
                    ))}
                  </div>
                </div>
              );
            }

            return (
              <Link key={link.to} to={link.to} className={`${isActive(link.to, link.children)} hover:!text-yellow-300 transition`}>
                {link.label}
              </Link>
            );
          })}
        </nav>
        <div className=" flex justify-center item-center space-x-1 sm:space-x-2">
          {hasPermission("notifications") && <NotificationDropdown />}
          {menuLinks.length === 0 ? (
            <>
              <button onClick={() => setModalOpen(true)} className="!px-4 !py-2 !rounded-lg !bg-red-800 !text-white hover:!bg-red-700 !transition cursor-pointer hidden md:block">
                Logout
              </button>
              <div className="relative block md:hidden" ref={dropdownRef}>
                <button onClick={() => setDropdownOpen(!dropdownOpen)} className="flex items-center gap-2 text-white rounded-md transition cursor-pointer">
                  <MenuIcon size={28} />
                </button>

                <div className={`absolute right-0 w-64 bg-white rounded-md shadow-lg overflow-hidden transition-all duration-300 origin-top-right z-[3] ${dropdownOpen ? "scale-100 opacity-100" : "scale-95 opacity-0 pointer-events-none"}`}>
                  <div className="block md:hidden">
                    {filterLinks(NAV_LINKS).map((item, i) => {
                      if (item.children?.length) {
                        return (
                          <div key={i} className="border-b border-gray-200">
                            {item.children.map((child, i) => (
                              <Link key={i} to={child.to} onClick={() => setDropdownOpen(false)} className={`block !px-4 !py-2 text-md text-left !text-gray-700 hover:bg-blue-100 transition ${location.pathname === child.to ? "bg-blue-100 font-medium !text-blue-700" : ""}`}>
                                {child.label}
                              </Link>
                            ))}
                          </div>
                        );
                      }

                      return (
                        <Link key={i} to={item.to} onClick={() => setDropdownOpen(false)} className={`block !px-4 !py-2 text-md text-left !text-gray-700 hover:bg-blue-100 transition ${location.pathname === item.to ? "bg-blue-100 font-medium !text-blue-700" : ""}`}>
                          {item.label}
                        </Link>
                      );
                    })}

                    <button onClick={() => setModalOpen(true)} className="block w-full text-left !px-4 !py-2 text-md !text-red-600 hover:bg-red-100 transition">
                      Logout
                    </button>
                  </div>

                  <div className="hidden md:block">
                    {menuLinks.map((item, i) => (
                      <Link key={i} to={item.to} onClick={() => setDropdownOpen(false)} className={`block !px-4 !py-2 text-md text-left !text-gray-700 hover:bg-blue-100 transition ${location.pathname === item.to ? "bg-blue-100 font-medium !text-blue-700" : ""}`}>
                        {item.label}
                      </Link>
                    ))}

                    <button onClick={() => setModalOpen(true)} className="block w-full text-left !px-4 !py-2 text-md !text-red-600 hover:bg-red-100 transition cursor-pointer">
                      Logout
                    </button>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="relative" ref={dropdownRef}>
              <button onClick={() => setDropdownOpen(!dropdownOpen)} className="flex items-center gap-2 text-white rounded-md transition cursor-pointer">
                <MenuIcon size={28} />
              </button>

              <div className={`absolute right-0 w-64 bg-white rounded-md shadow-lg overflow-hidden transition-all duration-300 origin-top-right z-[3] ${dropdownOpen ? "scale-100 opacity-100" : "scale-95 opacity-0 pointer-events-none"}`}>
                <div className="block md:hidden">
                  {filterLinks(NAV_LINKS).map((item, i) => {
                    if (item.children?.length) {
                      return (
                        <div key={i} className="border-b border-gray-200">
                          {item.children.map((child, i) => (
                            <Link key={i} to={child.to} onClick={() => setDropdownOpen(false)} className={`block !px-4 !py-2 text-md text-left !text-gray-700 hover:bg-blue-100 transition ${location.pathname === child.to ? "bg-blue-100 font-medium !text-blue-700" : ""}`}>
                              {child.label}
                            </Link>
                          ))}
                        </div>
                      );
                    }

                    return (
                      <Link key={i} to={item.to} onClick={() => setDropdownOpen(false)} className={`block !px-4 !py-2 text-md text-left !text-gray-700 hover:bg-blue-100 transition ${location.pathname === item.to ? "bg-blue-100 font-medium !text-blue-700" : ""}`}>
                        {item.label}
                      </Link>
                    );
                  })}

                  <button onClick={() => setModalOpen(true)} className="block w-full text-left !px-4 !py-2 text-md !text-red-600 hover:bg-red-100 transition">
                    Logout
                  </button>
                </div>

                <div className="hidden md:block max-h-72 overflow-y-auto">
                  {menuLinks.map((item, i) => (
                    <Link key={i} to={item.to} onClick={() => setDropdownOpen(false)} className={`block !px-4 !py-2 text-md text-left !text-gray-700 hover:bg-blue-100 transition ${location.pathname === item.to ? "bg-blue-100 font-medium !text-blue-700" : ""}`}>
                      {item.label}
                    </Link>
                  ))}
                  <button onClick={() => setModalOpen(true)} className="block w-full text-left !px-4 !py-2 text-md !text-red-600 hover:bg-red-100 transition cursor-pointer">
                    Logout
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        <LogoutModal isOpen={modalOpen} onConfirm={handleLogout} onCancel={() => setModalOpen(false)} />
      </header> */}

      <header className="relative shadow flex items-center justify-between pt-4 pb-5 px-5 md:px-8 text-white z-[6] sm:rounded-b-2xl">
        <div className="absolute inset-0 sm:rounded-b-2xl overflow-hidden pointer-events-none">
          <div
            className={`absolute inset-0 transition-transform duration-700 ease-in-out
        ${activeTheme ? "bg-sky-500 translate-y-0" : "bg-sky-500 -translate-y-full"}
      `}
          />

          <div
            className={`absolute inset-0 transition-transform duration-700 ease-in-out
        ${!activeTheme ? "bg-[#008080] translate-y-0" : "bg-[#008080] translate-y-full"}
      `}
          />
        </div>

        <div className="relative z-10 w-full flex items-center justify-between">
          <div className="text-3xl font-semibold">Real Estate</div>

          <nav className="hidden md:flex gap-6 relative z-30">
            {centerLinks.map((link, i) => {
              if (link.children?.length) {
                const parentActive = isActive(link.to, link.children);

                return (
                  <div key={i} className={`relative group transition ${parentActive.includes("!text-yellow-300") ? "!text-yellow-300" : "hover:text-yellow-300"}`} onMouseEnter={() => setChildrensOpen(i)} onMouseLeave={() => setChildrensOpen(null)}>
                    <button onClick={() => setChildrensOpen(childrensOpen === i ? null : i)} className={`transition cursor-pointer ${parentActive.includes("!text-yellow-300") ? "!text-yellow-300" : ""}`}>
                      {link.label}
                    </button>
                    <div className={`absolute left-0 min-w-42 bg-white rounded-md shadow-lg transform transition-all duration-200 origin-top z-[7] overflow-hidden ${childrensOpen === i ? "scale-100 max-h-96 opacity-100" : "scale-95 max-h-0 opacity-0 pointer-events-none"}`}>
                      {link.children.map((child, i) => (
                        <Link key={i} to={child.to} className={`block !px-4 !py-2 text-md text-center !text-gray-700 hover:bg-blue-100 transition text-nowrap ${location.pathname === child.to ? "bg-blue-100 font-medium !text-blue-700" : ""}`}>
                          {child.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                );
              }

              return (
                <Link key={link.to} to={link.to} className={`${isActive(link.to, link.children)} hover:!text-yellow-300 transition`}>
                  {link.label}
                </Link>
              );
            })}
          </nav>
          <div className=" flex justify-center item-center space-x-1 sm:space-x-2">
            {hasPermission("notifications") && <NotificationDropdown />}
            {menuLinks.length === 0 ? (
              <>
                <button onClick={() => setModalOpen(true)} className="!px-4 !py-2 !rounded-lg !bg-red-800 !text-white hover:!bg-red-700 !transition cursor-pointer hidden md:block">
                  Logout
                </button>
                <div className="relative block md:hidden" ref={dropdownRef}>
                  <button onClick={() => setDropdownOpen(!dropdownOpen)} className="flex items-center gap-2 text-white rounded-md transition cursor-pointer">
                    <MenuIcon size={28} />
                  </button>

                  <div className={`absolute right-0 w-64 bg-white rounded-md shadow-lg overflow-hidden transition-all duration-300 origin-top-right z-[7] ${dropdownOpen ? "scale-100 opacity-100" : "scale-95 opacity-0 pointer-events-none"}`}>
                    <div className="block md:hidden">
                      {filterLinks(NAV_LINKS).map((item, i) => {
                        if (item.children?.length) {
                          return (
                            <div key={i} className="border-b border-gray-200">
                              {item.children.map((child, i) => (
                                <Link key={i} to={child.to} onClick={() => setDropdownOpen(false)} className={`block !px-4 !py-2 text-md text-left !text-gray-700 hover:bg-blue-100 transition ${location.pathname === child.to ? "bg-blue-100 font-medium !text-blue-700" : ""}`}>
                                  {child.label}
                                </Link>
                              ))}
                            </div>
                          );
                        }

                        return (
                          <Link key={i} to={item.to} onClick={() => setDropdownOpen(false)} className={`block !px-4 !py-2 text-md text-left !text-gray-700 hover:bg-blue-100 transition ${location.pathname === item.to ? "bg-blue-100 font-medium !text-blue-700" : ""}`}>
                            {item.label}
                          </Link>
                        );
                      })}

                      <button onClick={() => setModalOpen(true)} className="block w-full text-left !px-4 !py-2 text-md !text-red-600 hover:bg-red-100 transition">
                        Logout
                      </button>
                    </div>

                    <div className="hidden md:block">
                      {menuLinks.map((item, i) => (
                        <Link key={i} to={item.to} onClick={() => setDropdownOpen(false)} className={`block !px-4 !py-2 text-md text-left !text-gray-700 hover:bg-blue-100 transition ${location.pathname === item.to ? "bg-blue-100 font-medium !text-blue-700" : ""}`}>
                          {item.label}
                        </Link>
                      ))}

                      <button onClick={() => setModalOpen(true)} className="block w-full text-left !px-4 !py-2 text-md !text-red-600 hover:bg-red-100 transition cursor-pointer">
                        Logout
                      </button>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <div className="relative" ref={dropdownRef}>
                <button onClick={() => setDropdownOpen(!dropdownOpen)} className="flex items-center gap-2 text-white rounded-md transition cursor-pointer">
                  <MenuIcon size={28} />
                </button>

                <div className={`absolute right-0 w-64 bg-white rounded-md shadow-lg overflow-hidden transition-all duration-300 origin-top-right z-[7] ${dropdownOpen ? "scale-100 opacity-100" : "scale-95 opacity-0 pointer-events-none"}`}>
                  <div className="block md:hidden">
                    {filterLinks(NAV_LINKS).map((item, i) => {
                      if (item.children?.length) {
                        return (
                          <div key={i} className="border-b border-gray-200">
                            {item.children.map((child, i) => (
                              <Link key={i} to={child.to} onClick={() => setDropdownOpen(false)} className={`block !px-4 !py-2 text-md text-left !text-gray-700 hover:bg-blue-100 transition ${location.pathname === child.to ? "bg-blue-100 font-medium !text-blue-700" : ""}`}>
                                {child.label}
                              </Link>
                            ))}
                          </div>
                        );
                      }

                      return (
                        <Link key={i} to={item.to} onClick={() => setDropdownOpen(false)} className={`block !px-4 !py-2 text-md text-left !text-gray-700 hover:bg-blue-100 transition ${location.pathname === item.to ? "bg-blue-100 font-medium !text-blue-700" : ""}`}>
                          {item.label}
                        </Link>
                      );
                    })}

                    <button onClick={() => setModalOpen(true)} className="block w-full text-left !px-4 !py-2 text-md !text-red-600 hover:bg-red-100 transition">
                      Logout
                    </button>
                  </div>

                  <div className="hidden md:block max-h-72 overflow-y-auto">
                    {menuLinks.map((item, i) => (
                      <Link key={i} to={item.to} onClick={() => setDropdownOpen(false)} className={`block !px-4 !py-2 text-md text-left !text-gray-700 hover:bg-blue-100 transition ${location.pathname === item.to ? "bg-blue-100 font-medium !text-blue-700" : ""}`}>
                        {item.label}
                      </Link>
                    ))}
                    <button onClick={() => setModalOpen(true)} className="block w-full text-left !px-4 !py-2 text-md !text-red-600 hover:bg-red-100 transition cursor-pointer">
                      Logout
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          <LogoutModal isOpen={modalOpen} onConfirm={handleLogout} onCancel={() => setModalOpen(false)} />
        </div>
      </header>
    </>
  );
}
